import { useQuery } from '@tanstack/react-query';
import { getModelProvider, toMastraModel } from '../lib/debugModels';

const OPENROUTER_MODELS_URL = 'https://openrouter.ai/api/v1/models';
const OPENROUTER_MODELS_QUERY_KEY = ['AI', 'OPENROUTER_MODELS'];

export interface OpenRouterModelOption {
  /** Mastra value, e.g. "openrouter/deepseek/deepseek-v4-flash". */
  value: string;
  /** Human-friendly name from the catalog, falling back to the raw id. */
  label: string;
  /** Author segment used for the models.dev logo. */
  provider?: string;
}

interface OpenRouterModelsResponse {
  data?: Array<{ id?: string; name?: string }>;
}

/**
 * Fetches the full OpenRouter model catalog and maps it to Mastra-prefixed
 * options. Dev-only; pass `enabled` to defer the request until the picker opens.
 */
export const useOpenRouterModels = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: OPENROUTER_MODELS_QUERY_KEY,
    queryFn: async (): Promise<OpenRouterModelOption[]> => {
      const response = await fetch(OPENROUTER_MODELS_URL);
      if (!response.ok) throw new Error(`OpenRouter models request failed: ${response.status}`);
      const json: OpenRouterModelsResponse = await response.json();
      return (json.data ?? [])
        .filter((model): model is { id: string; name?: string } => Boolean(model.id))
        .map((model) => {
          const value = toMastraModel(model.id);
          return { value, label: model.name?.trim() || model.id, provider: getModelProvider(value) };
        });
    },
    enabled,
    staleTime: 1000 * 60 * 60, // catalog rarely changes within a session
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
